import { ModelOption } from "src/genai/ModelSelector/types";

/** The underlying model id, stripped of any `:byok:<credential>` suffix. */
const baseModelId = (optionId: string): string => optionId.split(":byok:")[0];

/**
 * BYOK deployments bill through the customer's own provider and arrive without
 * pricing; backfill it from the shared (non-BYOK) model of the same id so the
 * comparison table still shows a cost.
 */
export const withByokPricing = (options: ModelOption[]): ModelOption[] => {
  const costByModelId = new Map(
    options
      .filter((option) => !option.byok)
      .map(
        (option) =>
          [
            option.id,
            {
              inputCostPer1MTokens: option.inputCostPer1MTokens,
              outputCostPer1MTokens: option.outputCostPer1MTokens,
            },
          ] as const,
      ),
  );

  return options.map((option) => {
    if (!option.byok) return option;
    const baseCost = costByModelId.get(baseModelId(option.id));
    return baseCost ? { ...option, ...baseCost } : option;
  });
};

/**
 * Merge the per-model tokens-per-minute quota into the options. Only shared
 * (non-BYOK) models carry the workspace quota — BYOK deployments route through
 * the customer's own provider, so the Taktile quota doesn't apply to them.
 */
export const withTpmQuota = (
  options: ModelOption[],
  tpmByModelId: Record<string, number>,
): ModelOption[] =>
  options.map((option) => ({
    ...option,
    tokensPerMinuteQuota: option.byok
      ? null
      : (tpmByModelId[option.id] ?? null),
  }));
